/**
 * Model Selection Service
 * 
 * Automatically selects the best AI model based on user input type.
 * This removes the need for manual model selection from the UI.
 */

import {
    CLAUDE_MODEL_SONNET,
    TEXT_MODEL_PRO,
    extractYouTubeUrls,
} from './geminiService';
import { extractInstagramUrls } from './instagramService';
import type { UploadedDocument } from '../types';

export interface InputAnalysis {
    hasYouTubeUrl: boolean;
    hasInstagramUrl: boolean;
    hasDocument: boolean;
    documentType?: 'pdf' | 'txt' | 'md' | 'image';
    isPlainText: boolean;
    youtubeUrls?: string[];
    instagramUrls?: string[];
}

export interface ModelSelection {
    model: string;
    reason: string;
    requiresGemini: boolean;
    requiresClaude: boolean;
}

/**
 * Analyzes user input to determine content type
 */
export function analyzeInput(
    topic: string,
    document?: UploadedDocument
): InputAnalysis {
    const youtubeUrls = extractYouTubeUrls(topic);
    const instagramUrls = extractInstagramUrls(topic);

    return {
        hasYouTubeUrl: youtubeUrls.length > 0,
        hasInstagramUrl: instagramUrls.length > 0,
        hasDocument: !!document,
        documentType: document?.type,
        isPlainText: !document && youtubeUrls.length === 0 && instagramUrls.length === 0,
        youtubeUrls,
        instagramUrls,
    };
}

/**
 * Selects the best AI model based on input analysis
 * 
 * Priority rules:
 * 1. YouTube URL → Gemini (only one that supports video)
 * 2. PDF Document → Gemini (only one that supports visual)
 * 3. Instagram URL → Claude (better copywriting after Apify scraping)
 * 4. Text documents (TXT/MD) → Claude (better text quality)
 * 5. Plain text topic → Claude (best quality, default)
 */
export function selectModel(analysis: InputAnalysis): ModelSelection {
    // Priority 1: YouTube URL → Claude (transcript extracted server-side, better copywriting)
    if (analysis.hasYouTubeUrl) {
        return {
            model: CLAUDE_MODEL_SONNET,
            reason: 'Claude Sonnet — Conteúdo a partir de transcrição do YouTube',
            requiresGemini: false,
            requiresClaude: true,
        };
    }

    // Priority 2: PDF Document → Gemini (only one that supports visual)
    if (analysis.hasDocument && analysis.documentType === 'pdf') {
        return {
            model: TEXT_MODEL_PRO,
            reason: 'Gemini Pro — Análise visual do PDF',
            requiresGemini: true,
            requiresClaude: false,
        };
    }

    // Priority 2.5: Image Document → Gemini (vision API for OCR)
    if (analysis.hasDocument && analysis.documentType === 'image') {
        return {
            model: TEXT_MODEL_PRO,
            reason: 'Gemini Pro — Análise visual da imagem',
            requiresGemini: true,
            requiresClaude: false,
        };
    }

    // Priority 3: Instagram URL → Claude (better copywriting after Apify scraping)
    if (analysis.hasInstagramUrl) {
        return {
            model: CLAUDE_MODEL_SONNET,
            reason: 'Claude Sonnet — Melhor qualidade para conteúdo do Instagram',
            requiresGemini: false,
            requiresClaude: true,
        };
    }

    // Priority 4: Text documents (TXT/MD) → Claude (better text quality)
    if (analysis.hasDocument && (analysis.documentType === 'txt' || analysis.documentType === 'md')) {
        return {
            model: CLAUDE_MODEL_SONNET,
            reason: 'Claude Sonnet — Melhor qualidade de texto',
            requiresGemini: false,
            requiresClaude: true,
        };
    }

    // Default: Plain text topic → Claude (best quality)
    return {
        model: CLAUDE_MODEL_SONNET,
        reason: 'Claude Sonnet — Melhor qualidade geral',
        requiresGemini: false,
        requiresClaude: true,
    };
}
